import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import api from '@/services/api';

export type AlignmentRoadmapScope = {
  mode: 'all' | 'selected';
  roadmapIds: string[];
};

export type AlignmentRoadmapOption = {
  id: string;
  name: string;
  status: string;
};

type ScopeContextValue = {
  scope: AlignmentRoadmapScope;
  setScope: (next: AlignmentRoadmapScope) => void;
  roadmaps: AlignmentRoadmapOption[];
  /** Changes when the user picks a different roadmap set — use as a fetch dep. */
  scopeKey: string;
  listParams: { params?: { roadmapIds: string } };
  reportBody: { roadmap_ids?: string[] };
};

const DEFAULT_SCOPE: AlignmentRoadmapScope = { mode: 'all', roadmapIds: [] };

const STORAGE_PREFIX = 'tmos.alignment.roadmapScope.';

function loadScope(tenantId: string): AlignmentRoadmapScope {
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + tenantId);
    if (!raw) {
      return DEFAULT_SCOPE;
    }
    const parsed = JSON.parse(raw) as AlignmentRoadmapScope;
    if (parsed.mode === 'selected' && Array.isArray(parsed.roadmapIds) && parsed.roadmapIds.length > 0) {
      return {
        mode: 'selected',
        roadmapIds: parsed.roadmapIds.filter((id) => typeof id === 'string')
      };
    }
  } catch {
    // Corrupt prefs should not break the page — fall back to all roadmaps.
  }
  return DEFAULT_SCOPE;
}

function saveScope(tenantId: string, scope: AlignmentRoadmapScope): void {
  window.localStorage.setItem(STORAGE_PREFIX + tenantId, JSON.stringify(scope));
}

export function alignmentRoadmapQueryParams(
  scope: AlignmentRoadmapScope
): { roadmapIds?: string } {
  if (scope.mode !== 'selected' || scope.roadmapIds.length === 0) {
    return {};
  }
  return { roadmapIds: scope.roadmapIds.join(',') };
}

export function alignmentRoadmapBody(
  scope: AlignmentRoadmapScope
): { roadmap_ids?: string[] } {
  if (scope.mode !== 'selected' || scope.roadmapIds.length === 0) {
    return {};
  }
  return { roadmap_ids: scope.roadmapIds };
}

const AlignmentRoadmapScopeContext = createContext<ScopeContextValue>({
  scope: DEFAULT_SCOPE,
  setScope: () => undefined,
  roadmaps: [],
  scopeKey: 'all',
  listParams: {},
  reportBody: {}
});

export function AlignmentRoadmapScopeProvider({
  tenantId,
  children
}: {
  tenantId: string;
  children: React.ReactNode;
}): React.ReactElement {
  const [scope, setScopeState] = useState<AlignmentRoadmapScope>(() =>
    tenantId ? loadScope(tenantId) : DEFAULT_SCOPE
  );
  const [roadmaps, setRoadmaps] = useState<AlignmentRoadmapOption[]>([]);

  useEffect(() => {
    setScopeState(tenantId ? loadScope(tenantId) : DEFAULT_SCOPE);
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) {
      setRoadmaps([]);
      return;
    }
    let cancelled = false;
    const loadRoadmaps = async () => {
      try {
        const response = await api.get('/tenant/roadmaps');
        const rows = (response.data?.data || []) as Array<{
          id: string;
          name: string;
          status?: string;
        }>;
        if (cancelled) {
          return;
        }
        // Archived/inactive roadmaps stay out of the picker; alignment stays on live work.
        setRoadmaps(
          rows
            .filter((row) => row.status !== 'ARCHIVED' && row.status !== 'INACTIVE')
            .map((row) => ({
              id: row.id,
              name: row.name,
              status: row.status || 'ACTIVE'
            }))
        );
      } catch {
        if (!cancelled) {
          setRoadmaps([]);
        }
      }
    };
    void loadRoadmaps();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const setScope = useCallback(
    (next: AlignmentRoadmapScope) => {
      const normalized: AlignmentRoadmapScope =
        next.mode === 'selected' && next.roadmapIds.length > 0
          ? { mode: 'selected', roadmapIds: next.roadmapIds }
          : DEFAULT_SCOPE;
      setScopeState(normalized);
      if (tenantId) {
        saveScope(tenantId, normalized);
      }
    },
    [tenantId]
  );

  const value = useMemo<ScopeContextValue>(() => {
    const query = alignmentRoadmapQueryParams(scope);
    return {
      scope,
      setScope,
      roadmaps,
      scopeKey: scope.mode === 'all' ? 'all' : scope.roadmapIds.slice().sort().join(','),
      listParams: query.roadmapIds ? { params: { roadmapIds: query.roadmapIds } } : {},
      reportBody: alignmentRoadmapBody(scope)
    };
  }, [scope, setScope, roadmaps]);

  return (
    <AlignmentRoadmapScopeContext.Provider value={value}>
      {children}
    </AlignmentRoadmapScopeContext.Provider>
  );
}

export function useAlignmentRoadmapScope(): ScopeContextValue {
  return useContext(AlignmentRoadmapScopeContext);
}
